import { RankKit } from "@/components/site/RankKit";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand, FAQ, SectionHeader } from "@/components/site/Blocks";
import { CalcCard, CalcField, CalcRow, CalcStat, usd, pct } from "@/components/site/CalcKit";
import { ToolLeadGate } from "@/components/site/ToolLeadGate";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { ShareResultsBar } from "@/components/site/ShareResultsBar";
import { CalculatorAiPanel } from "@/components/site/CalculatorAiPanel";
import { CalculatorStateGate } from "@/components/site/CalculatorStateGate";
import { trackCalcCta, trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import cashOnCashHero from "@/assets/hero/hero-cash-on-cash-phone.jpg";

const FAQS = [
  {
    q: "What is a good cash-on-cash return on a rental property?",
    a: "Most buy-and-hold investors underwrite to 8%–12% cash-on-cash in year one, with anything above 12% considered strong for a stabilized long-term rental. Short-term and mid-term rentals are usually underwritten higher (12%–20%) because operating volatility and management effort are higher. The right target depends on your market, leverage, and whether you are buying for cash flow or appreciation.",
  },
  {
    q: "How is cash-on-cash return calculated?",
    a: "Cash-on-cash return = annual pre-tax cash flow ÷ total cash invested. Annual cash flow is rent minus vacancy, operating expenses, and debt service. Total cash invested is your down payment plus closing costs, plus any rehab or furnishing capital you put in out of pocket.",
  },
  {
    q: "What is the difference between cash-on-cash return and cap rate?",
    a: "Cap rate ignores financing — it is NOI ÷ purchase price, so it measures the asset. Cash-on-cash includes your loan payment and only counts the cash you actually put in, so it measures your position in the deal. Two investors can buy the same property at the same cap rate and post very different cash-on-cash returns depending on leverage.",
  },
  {
    q: "Does cash-on-cash return include principal paydown or appreciation?",
    a: "No. Cash-on-cash is a pre-tax cash metric only. Principal paydown, appreciation, and tax benefits such as depreciation are real components of total return, but they are tracked separately — commonly through internal rate of return (IRR) or an equity-multiple analysis.",
  },
  {
    q: "How does a DSCR loan affect cash-on-cash return?",
    a: "Leverage magnifies the metric in both directions. A higher loan-to-value reduces the cash you invest, which usually raises cash-on-cash as long as the property still cash-flows after the payment. Interest-only DSCR structures can raise early-year cash-on-cash further, though the loan balance is not amortizing. Any figures shown here are estimates and are not an offer of credit.",
  },
  {
    q: "How does an interest-only DSCR loan change cash-on-cash return calculations?",
    a: "Interest-only payments are lower than a fully amortizing payment at the same rate, which increases year-one pre-tax cash flow and therefore cash-on-cash return during the interest-only period. Once the loan converts to amortizing or matures, the payment typically rises and cash-on-cash return falls unless rent has grown to offset it.",
  },
  {
    q: "What counts as total cash invested for a BRRRR deal?",
    a: "For a refinance-based strategy, total cash invested is usually the cash left in the deal after the cash-out refinance closes — purchase price plus rehab plus closing costs, minus the new loan proceeds. A successful BRRRR can post a very high or even infinite cash-on-cash return if most or all capital is returned at refinance.",
  },
  {
    q: "How does vacancy assumption change cash-on-cash return?",
    a: "Raising the vacancy percentage directly lowers effective gross income and therefore annual cash flow, so a property that looks strong at 3% vacancy can post a materially lower cash-on-cash return at a more conservative 7%–8% assumption. Use a vacancy rate grounded in the submarket's actual rental data rather than a generic default.",
  },
  {
    q: "Why is my cash-on-cash return negative even though the property has positive NOI?",
    a: "Cash-on-cash return can go negative when debt service exceeds NOI, meaning the property is cash-flow negative on a monthly basis even though it may still show acceptable cap rate or equity growth potential. This is common with higher-leverage purchases in markets where cap rates sit close to or below prevailing borrowing costs.",
  },
  {
    q: "How do closing costs affect cash-on-cash return?",
    a: "Closing costs increase the denominator (total cash invested) without adding to annual cash flow, so higher closing costs mechanically reduce the resulting cash-on-cash percentage even if the property performs identically. This is one reason investors negotiate seller or lender credits on properties where cash-on-cash is a key underwriting metric.",
  },
  {
    q: "What is a realistic cash-on-cash return for a short-term rental compared to a long-term rental?",
    a: "Short-term rentals often target higher cash-on-cash figures, commonly in the mid-teens to 20%-plus range, to compensate for greater income volatility, higher operating costs, and local regulatory risk. Long-term rentals typically underwrite to steadier but lower figures because occupancy and expenses are more predictable year over year.",
  },
  {
    q: "Does paying points to lower the interest rate improve cash-on-cash return?",
    a: "Paying discount points raises upfront cash invested but can lower the monthly payment enough to increase annual cash flow, so the net effect on cash-on-cash return depends on your expected hold period. A shorter hold generally favors fewer points, while a longer hold can make points pay off through improved annual cash flow.",
  },
];

export const Route = createFileRoute("/cash-on-cash-return-calculator")({
  head: () =>
    buildHead({
      title: "Cash-on-Cash Return Calculator for Rental Property",
      description:
        "Free cash-on-cash return calculator for rental property investors. Model rent, expenses, financing, and total cash invested to see year-one CoC return and cash flow.",
      url: "/cash-on-cash-return-calculator",
      keywords:
        "cash on cash return calculator, cash on cash return rental property, CoC return calculator, rental property return calculator, cash flow calculator rental, what is a good cash on cash return, investment property cash on cash, cash on cash vs cap rate, rental property ROI calculator, real estate cash flow calculator, DSCR loan cash on cash",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Cash-on-Cash Return Calculator", url: "/cash-on-cash-return-calculator" },
      ],
      service: { name: "Cash-on-Cash Return Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Cash-on-Cash Return Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/cash-on-cash-return-calculator",
          description:
            "Free calculator for year-one cash-on-cash return on a rental property, including financing and total cash invested.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: CashOnCashPage,
});

type Inputs = {
  purchasePrice: number;
  downPct: number;
  rate: number;
  termYears: number;
  interestOnly: boolean;
  closingCostsPct: number;
  rehab: number;
  monthlyRent: number;
  otherIncomeMo: number;
  taxesAnnual: number;
  insAnnual: number;
  hoaMonthly: number;
  vacancyPct: number;
  mgmtPct: number;
  maintenancePct: number;
};

const DEFAULT: Inputs = {
  purchasePrice: 320000,
  downPct: 25,
  rate: 7.5,
  termYears: 30,
  interestOnly: false,
  closingCostsPct: 3,
  rehab: 8000,
  monthlyRent: 2650,
  otherIncomeMo: 0,
  taxesAnnual: 3600,
  insAnnual: 1600,
  hoaMonthly: 0,
  vacancyPct: 5,
  mgmtPct: 8,
  maintenancePct: 5,
};

function amortPayment(principal: number, aprPct: number, years: number) {
  if (principal <= 0) return 0;
  const r = aprPct / 100 / 12;
  const n = years * 12;
  if (r <= 0) return principal / n;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

function CashOnCashPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("cash_on_cash", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("cash_on_cash", String(k), v as never);
  };

  const m = useMemo(() => {
    const down = i.purchasePrice * (i.downPct / 100);
    const loan = i.purchasePrice - down;
    const closingCosts = i.purchasePrice * (i.closingCostsPct / 100);
    const cashInvested = down + closingCosts + i.rehab;

    const pi = i.interestOnly ? (loan * (i.rate / 100)) / 12 : amortPayment(loan, i.rate, i.termYears);
    const taxesMo = i.taxesAnnual / 12;
    const insMo = i.insAnnual / 12;
    const grossMo = i.monthlyRent + i.otherIncomeMo;
    const vacancy = grossMo * (i.vacancyPct / 100);
    const mgmt = grossMo * (i.mgmtPct / 100);
    const maint = grossMo * (i.maintenancePct / 100);
    const opex = mgmt + maint + taxesMo + insMo + i.hoaMonthly;
    const noiMo = grossMo - vacancy - opex;
    const cashFlowMo = noiMo - pi;

    const coc = cashInvested > 0 ? (cashFlowMo * 12) / cashInvested : 0;
    const capRate = i.purchasePrice > 0 ? (noiMo * 12) / i.purchasePrice : 0;
    const pitia = pi + taxesMo + insMo + i.hoaMonthly;
    const dscr = pitia > 0 ? grossMo / pitia : 0;
    const grm = grossMo > 0 ? i.purchasePrice / (grossMo * 12) : 0;
    const onePctRule = i.purchasePrice > 0 ? grossMo / i.purchasePrice : 0;
    const breakevenRent = opex + pi + vacancy;

    return {
      down, loan, closingCosts, cashInvested, pi, taxesMo, insMo, grossMo, vacancy,
      opex, noiMo, cashFlowMo, coc, capRate, pitia, dscr, grm, onePctRule, breakevenRent,
    };
  }, [i]);

  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackCalcEstimateViewed("cash_on_cash", { coc: Number((m.coc * 100).toFixed(2)), dscr: Number(m.dscr.toFixed(2)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aiFacts = [
    { label: "Cash invested", value: usd(m.cashInvested) },
    { label: "Loan amount", value: usd(m.loan) },
    { label: "Estimated principal & interest", value: usd(m.pi) },
    { label: "Estimated PITIA", value: usd(m.pitia) },
    { label: "Monthly cash flow", value: usd(m.cashFlowMo) },
    { label: "Cash-on-cash return", value: pct(m.coc) },
    { label: "Cap rate", value: pct(m.capRate) },
    { label: "DSCR", value: m.dscr.toFixed(2) },
    { label: "Breakeven rent", value: usd(m.breakevenRent) },
  ];


  const cocColor = m.coc >= 0.12 ? "text-emerald-600" : m.coc >= 0.08 ? "text-amber-600" : "text-destructive";
  const dscrColor = m.dscr >= 1.2 ? "text-emerald-600" : m.dscr >= 1.0 ? "text-amber-600" : "text-destructive";

  return (
    <SiteShell>
      <PageHero
        eyebrow="Cash-on-cash calculator"
        title="Cash-on-Cash Return Calculator for Rental Property"
        subtitle="Enter the purchase, financing, rent and operating assumptions to see year-one cash-on-cash return, monthly cash flow, cap rate and DSCR side by side. Figures are illustrative estimates, not an offer of credit."
        primaryHref="/apply?product=dscr-loans"
        primaryLabel="Get Your Personalized Rate Quote"
        secondaryHref="/rates"
        secondaryLabel="See Today's Rates"
        image={{
          src: cashOnCashHero,
          alt: "Rental property investor using the Simply Approved Mortgages cash-on-cash calculator on a phone",
        }}
      />
      <RankKit page="cash-on-cash-return-calculator" part="answer" />
      <PageEssentials hideAnswer />

      <Section>
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-6">
            <CalcCard title="1. Purchase & financing">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Purchase price" value={i.purchasePrice} onChange={(v) => set("purchasePrice", v)} prefix="$" />
                <CalcField label="Down payment" value={i.downPct} onChange={(v) => set("downPct", v)} suffix="%" step={1} />
                <CalcField label="Interest rate" value={i.rate} onChange={(v) => set("rate", v)} suffix="%" step={0.125} />
                <CalcField label="Term (years)" value={i.termYears} onChange={(v) => set("termYears", v)} step={1} />
                <CalcField label="Closing costs" value={i.closingCostsPct} onChange={(v) => set("closingCostsPct", v)} suffix="%" step={0.25} />
                <CalcField label="Upfront rehab / turn cost" value={i.rehab} onChange={(v) => set("rehab", v)} prefix="$" />
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={i.interestOnly}
                  onChange={(e) => set("interestOnly", e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Interest-only payment (common on DSCR programs)
              </label>
            </CalcCard>

            <CalcCard title="2. Income">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Monthly rent" value={i.monthlyRent} onChange={(v) => set("monthlyRent", v)} prefix="$" />
                <CalcField label="Other monthly income" value={i.otherIncomeMo} onChange={(v) => set("otherIncomeMo", v)} prefix="$" hint="Pet rent, parking, storage, laundry." />
              </div>
            </CalcCard>

            <CalcCard title="3. Operating expenses">
              <div className="grid gap-4 md:grid-cols-2">
                <CalcField label="Property taxes (annual)" value={i.taxesAnnual} onChange={(v) => set("taxesAnnual", v)} prefix="$" />
                <CalcField label="Insurance (annual)" value={i.insAnnual} onChange={(v) => set("insAnnual", v)} prefix="$" />
                <CalcField label="HOA (monthly)" value={i.hoaMonthly} onChange={(v) => set("hoaMonthly", v)} prefix="$" />
                <CalcField label="Vacancy" value={i.vacancyPct} onChange={(v) => set("vacancyPct", v)} suffix="%" step={1} />
                <CalcField label="Property management" value={i.mgmtPct} onChange={(v) => set("mgmtPct", v)} suffix="%" step={1} />
                <CalcField label="Maintenance / capex" value={i.maintenancePct} onChange={(v) => set("maintenancePct", v)} suffix="%" step={1} />
              </div>
            </CalcCard>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <CalculatorStateGate calculator="cash_on_cash">
            <ToolLeadGate
              calculator="cash_on_cash"
              storageKey="coc-calc:lead-unlocked"
              product="Cash-on-Cash Calculator"
              sourcePage="Cash-on-Cash Return Calculator"
              title="Unlock your full return analysis"
              subtitle="See cash-on-cash, cap rate, DSCR, break-even rent and the full expense breakdown — and we'll email you a copy."
              getContext={() => ({
                purchase_price: i.purchasePrice,
                down_pct: i.downPct,
                monthly_rent: i.monthlyRent,
                cash_invested: Math.round(m.cashInvested),
                cash_on_cash: (m.coc * 100).toFixed(1) + "%",
                dscr: m.dscr.toFixed(2),
              })}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <CalcStat label="Cash-on-cash (yr 1)" value={pct(m.coc, 1)} colorClass={cocColor} />
                  <CalcStat label="Monthly cash flow" value={usd(m.cashFlowMo)} colorClass={m.cashFlowMo >= 0 ? "text-emerald-600" : "text-destructive"} />
                  <CalcStat label="Cap rate" value={pct(m.capRate)} />
                  <CalcStat label="DSCR (rent ÷ PITIA)" value={m.dscr.toFixed(2)} colorClass={dscrColor} />
                </div>

                <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <h3 className="font-display text-base font-semibold text-foreground">Deal breakdown</h3>
                  <dl className="mt-3 space-y-1.5 text-sm">
                    <CalcRow label="Down payment" value={usd(m.down)} />
                    <CalcRow label="Closing costs" value={usd(m.closingCosts)} />
                    <CalcRow label="Upfront rehab" value={usd(i.rehab)} />
                    <CalcRow label="Total cash invested" value={usd(m.cashInvested)} strong />
                    <CalcRow label="Loan amount" value={usd(m.loan)} />
                    <CalcRow label={i.interestOnly ? "Monthly interest-only payment" : "Monthly P&I"} value={usd(m.pi)} />
                    <CalcRow label="Monthly PITIA" value={usd(m.pitia)} />
                    <CalcRow label="Monthly operating expenses" value={usd(m.opex)} />
                    <CalcRow label="Monthly NOI" value={usd(m.noiMo)} />
                    <CalcRow label="Annual cash flow" value={usd(m.cashFlowMo * 12)} strong colorClass={m.cashFlowMo >= 0 ? "text-emerald-600" : "text-destructive"} />
                    <CalcRow label="Break-even monthly rent" value={usd(m.breakevenRent)} />
                    <CalcRow label="Gross rent multiplier" value={m.grm.toFixed(1)} />
                    <CalcRow label="1% rule" value={pct(m.onePctRule)} />
                  </dl>
                </div>

                <Link
                  onClick={() => trackCalcCta("cash_on_cash", "related_tool")}
                  to="/dscr-calculator"
                  className="block rounded-md bg-brand px-4 py-3 text-center text-sm font-semibold text-primary-foreground hover:bg-brand/90"
                >
                  Check DSCR loan qualification →
                </Link>
                <Link
                  onClick={() => trackCalcCta("cash_on_cash", "apply")}
                  to="/apply"
                  search={{ product: "dscr-loans" }}
                  className="block rounded-md border border-brand-gold bg-brand-gold px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-brand-gold-hover"
                >
                  Get Your Personalized Rate Quote
                </Link>
              </div>
            </ToolLeadGate>
            <ShareResultsBar
              calculator="cash_on_cash"
              shareUrl={shareUrl}
              subject="My cash-on-cash scenario"
              summaryTitle="Cash-on-cash scenario — key numbers"
              keyNumbers={aiFacts.slice(0, 6)}
            />
            <CalculatorAiPanel
              calculator="cash_on_cash"
              facts={aiFacts}
              suggestions={[
                  "What is the difference between cash-on-cash return and cap rate?",
                  "How does leverage change my cash-on-cash return?",
                  "What does this DSCR figure mean for financing?",
                  "Which inputs move breakeven rent the most?",
                ]}
            />
            </CalculatorStateGate>
          </aside>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl space-y-6">
          <SectionHeader
            title="How to read your cash-on-cash return"
            intro="Cash-on-cash return answers one question: for every dollar of your own money in the deal, how many cents of pre-tax cash come back this year?"
          />
          <div className="space-y-4 text-sm text-foreground/90">
            <p>
              The formula is simple — <strong>annual pre-tax cash flow ÷ total cash invested</strong> — but the inputs are where
              investors get burned. Two mistakes account for most inflated projections: leaving out vacancy and capital
              reserves, and forgetting that closing costs and the first turn are real cash out of pocket, not financed dollars.
            </p>
            <p>
              A stabilized long-term rental underwritten honestly usually lands between 6% and 12% cash-on-cash in year one at
              today&apos;s leverage levels. Short-term and mid-term rentals target higher because they carry furnishing capital,
              higher turnover, and more operating risk. If a deal only clears your target after you delete vacancy, management
              and maintenance, it does not clear your target.
            </p>
            <p>
              Leverage is the biggest lever. Because the denominator is your cash, a lower down payment can raise cash-on-cash
              even as monthly cash flow falls — right up to the point the payment eats the cash flow entirely. That is why we
              pair this calculator with DSCR: cash-on-cash tells you whether the deal is worth your capital, and{" "}
              <Link to="/dscr-calculator" className="text-brand underline">DSCR</Link> tells you whether a lender will finance it.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { t: "Under 6%", b: "Thin. Usually an appreciation play, a below-market rent to fix, or an expense assumption that needs a second look." },
              { t: "6%–12%", b: "The normal band for a stabilized long-term rental with 20–25% down in most of our lending markets." },
              { t: "12%+", b: "Strong. Verify the rent comp, taxes at the reassessed value, and insurance quote before you count on it." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <div className="font-display text-lg font-semibold text-foreground">{c.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <RankKit page="cash-on-cash-return-calculator" part="details" />
      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow="Live pricing · DSCR"
        heading="Price the financing behind your return"
        paragraphs={[
          "These are pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property. Rate, points or lender credit, and estimated principal and interest come from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/cap-rate-calculator", label: "Run the cap rate on this property" },
        ]}
      />

      <Section>
        <div className="mx-auto max-w-4xl">
          <SectionHeader title="Cash-on-cash return FAQ" intro="The questions investors ask us most about this metric." />
          <div className="mt-6">
            <FAQ items={FAQS} />
          </div>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-foreground">Keep underwriting</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/cap-rate-calculator" as const, label: "Cap rate calculator — measure the asset without financing" },
              { to: "/arv-calculator" as const, label: "ARV calculator — after-repair value and the 70% rule" },
              { to: "/dscr-calculator" as const, label: "DSCR calculator — will the loan qualify?" },
              { to: "/brrrr-calculator" as const, label: "BRRRR calculator — model the full refinance loop" },
              { to: "/best-rental-markets" as const, label: "Best rental markets — where these returns are showing up" },
              { to: "/dscr-loan-requirements" as const, label: "DSCR loan requirements — FICO, LTV, reserves" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-brand underline">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <CTABand
        title="Run the numbers with a licensed loan officer"
        body="Share the scenario and we'll return an illustrative DSCR structure for it — no obligation, no hard credit pull, business-purpose investment property only."
      />
    <PageOutro />
      </SiteShell>
  );
}
